// frontend/src/context/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { UserInfo } from '../types'; // Make sure your UserInfo type is defined here

interface AuthContextType {
    user: UserInfo | null;
    login: (userData: UserInfo) => void;
    logout: () => void;
    updateUserWallet: (newBalance: number) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<UserInfo | null>(() => {
        // Initialize user from localStorage using 'userInfo' key
        const storedUserInfo = localStorage.getItem('userInfo');
        return storedUserInfo ? JSON.parse(storedUserInfo) : null;
    });

    useEffect(() => {
        // Update localStorage whenever user state changes using 'userInfo' key
        if (user) {
            localStorage.setItem('userInfo', JSON.stringify(user));
        } else {
            localStorage.removeItem('userInfo');
        }
    }, [user]);

    const login = (userData: UserInfo) => {
        setUser(userData);
    };

    const logout = () => {
        setUser(null);
        // localStorage.removeItem('userInfo'); // This is handled by the useEffect
    };

    const updateUserWallet = (newBalance: number) => {
        setUser(prevUser => {
            if (prevUser) {
                return { ...prevUser, walletBalance: newBalance };
            }
            return null;
        });
    };

    return (
        <AuthContext.Provider value={{ user, login, logout, updateUserWallet }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};